import { EventEmitter, h, Component, Host, Prop, Watch, Event, State } from '@stencil/core';

@Component({
  tag: 'pk-checkbox',
  styleUrl: './scss/pk-checkbox.scss',
  shadow: true,
})
export class PkCheckbox {
  // Own Properties
  // ====================

  private checkboxElement: HTMLInputElement;
  private prevCheckedValue: boolean;

  // Reference to host HTML element
  // ===================================

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() isFocused: boolean;
  @State() hasLabel: boolean;

  // Public Property API
  // ========================

  /** Will set the initial checked status of the checkbox element */
  @Prop({ reflect: true, mutable: true }) checked: boolean;

  /** Will disable the checkbox element */
  @Prop({ reflect: true }) disabled: boolean;

  /** A state that is neither checked nor unchecked */
  @Prop({ mutable: true }) indeterminate: boolean;

  /** Name of the checkbox */
  @Prop({ reflect: true }) name!: string;

  /** Set unique id for every checkbox*/
  @Prop({ reflect: true }) uid!: string;

  /** Checkbox value*/
  @Prop({ reflect: true }) value!: string;

  // Prop lifecycle events
  // =======================

  @Watch('checked')
  setCheckUncheck(value: boolean) {
    if (!this.checkboxElement) return;

    this.checked = value;
    this.checkboxElement.checked = this.checked;

    if (this.checked) {
      this.setIndeterminate(false);
    }
  }

  @Watch('indeterminate')
  setIndeterminate(value: boolean) {
    if (!this.checkboxElement) return;

    this.indeterminate = value;
    this.checkboxElement.indeterminate = this.indeterminate;
    if (this.indeterminate) {
      this.checked = false;
    }
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** On checked/unchecked change event emitter */
  @Event() checkboxChange: EventEmitter<{ isChecked: boolean }>;

  /** Emit the value when checkbox is checked */
  @Event() checkboxValue: EventEmitter<{ value: string }>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    if (!this.name) {
      console.error('Please  provide a `name` property/attribute');
    }
    this.prevCheckedValue = this.checked;
  }

  componentDidLoad() {
    this.hasLabel = this.displayLabel();
  }

  componentDidUpdate() {
    if (this.checked !== this.prevCheckedValue) {
      if (!this.indeterminate) {
        this.checkboxChange.emit({ isChecked: this.checked });
      }
      this.prevCheckedValue = this.checked;

      if (this.checked) {
        this.checkboxValue.emit({ value: this.value });
      }
    }
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private onFocus = () => {
    if (this.disabled) return;

    this.isFocused = true;
  };

  private handleOnClick = (ev?: UIEvent) => {
    if (this.disabled) return;

    this.setCheckUncheck((ev.target as HTMLInputElement).checked);
  };

  private displayLabel = () => this.checkboxElement.querySelector('span.checkbox-text')?.children.length > 0;

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <Host
        class={{
          'checkbox-label': true,
          'is-disabled': this.disabled,
        }}
        aria-disabled={this.disabled ? 'true' : 'false'}
        aria-checked={this.checked ? 'true' : 'false'}
      >
        <label
          htmlFor={this.uid}
          class={{
            'cursor-pointer': !this.disabled,
            'cursor-not-allowed': this.disabled,
          }}
          part="label"
        >
          <input
            type="checkbox"
            value={this.value}
            name={this.name}
            id={this.uid}
            checked={this.checked}
            disabled={this.disabled}
            indeterminate={this.indeterminate}
            onFocus={this.onFocus}
            onChange={this.handleOnClick}
            ref={(el: HTMLInputElement) => (this.checkboxElement = el)}
            part="input"
          />
          <span
            class={{
              'checkbox-custom': true,
              'is-disabled': this.disabled,
            }}
            part="custom"
          />
          <span class={{ 'checkbox-text': true, 'is-disabled': this.disabled }} aria-hidden={this.hasLabel} part="text">
            <slot />
          </span>
          {/* to fix jsx-a11y/label-has-associated-control */}
        </label>
      </Host>
    );
  }
}
